import type {Band} from './economy';
import {courseForGrade, DEFAULT_TRACK, type Track} from './math/course-catalog';

// A child's school grade: -1 is Pre-K, 0 is kindergarten, 1-12 are grades, 13 is beyond high school.
// Grade picks the starting course (course-catalog.ts) and derives the reward band, which stays for the
// economy defaults. Safe for the client to import.
export const MIN_GRADE = -1, MAX_GRADE = 13;
export const isGrade = (value: unknown): value is number => Number.isInteger(value) && (value as number) >= MIN_GRADE && (value as number) <= MAX_GRADE;

const ordinal = (n: number) => `${n}${n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th'}`;
export function gradeLabel(grade: number): string {
  if (grade === -1) return 'Pre-K';
  if (grade === 0) return 'Kindergarten';
  if (grade === 13) return 'Beyond high school';
  return `${ordinal(grade)} grade`;
}
export const GRADES = Array.from({length: MAX_GRADE - MIN_GRADE + 1}, (_, i) => i + MIN_GRADE).map(value => ({value, label: gradeLabel(value)}));

export function bandForGrade(grade: number): Band {
  if (grade <= 0) return 'A';
  if (grade <= 2) return 'B';
  if (grade <= 5) return 'C';
  if (grade <= 8) return 'D';
  return 'E';
}

// For children added before grade was recorded: the lowest grade of their band. Parents are asked to
// confirm it (students.grade_confirmed = 0).
export const GRADE_FROM_BAND: Record<Band, number> = {A: 0, B: 1, C: 3, D: 6, E: 9};

// The one rule for a child's starting course: their grade's course on their track (Traditional unless the
// parent chose another), or for a child saved before grades existed, the lowest grade of their band.
// Grades K-8 are the same on every track. Every "which course is this child in, before they have a math
// profile" question asks this.
export const startingCourse = (child: {grade?: number | null; band: Band}, track: Track = DEFAULT_TRACK) => courseForGrade(child.grade ?? GRADE_FROM_BAND[child.band], track);
